import { useState, useEffect, useRef, useCallback, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export interface ContextMenuItem {
    label: string
    icon?: string
    checked?: boolean
    disabled?: boolean
    onClick: () => void
}

interface ContextMenuProps {
    items: ContextMenuItem[]
    children: ReactNode
}

/**
 * Reusable context menu with right-click (PC) and long-press (mobile/Mac) support.
 * Renders the menu via portal to avoid z-index issues.
 * Menu is viewport-clamped on all edges.
 */
export default function ContextMenu({ items, children }: ContextMenuProps) {
    const [open, setOpen] = useState(false)
    const [pos, setPos] = useState({ x: 0, y: 0 })
    const menuRef = useRef<HTMLDivElement>(null)
    const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)

    // ── Viewport-clamped positioning ──────────────────────────────────
    const showMenu = useCallback((clientX: number, clientY: number) => {
        setPos({ x: clientX, y: clientY })
        setOpen(true)
    }, [])

    // After opening, measure the menu and clamp to viewport
    useEffect(() => {
        if (!open || !menuRef.current) return
        const el = menuRef.current
        const rect = el.getBoundingClientRect()
        const pad = 8 // min distance from viewport edge

        let x = pos.x
        let y = pos.y

        // Clamp right edge
        if (x + rect.width > window.innerWidth - pad) {
            x = window.innerWidth - rect.width - pad
        }
        // Clamp left edge
        if (x < pad) x = pad

        // Clamp bottom edge
        if (y + rect.height > window.innerHeight - pad) {
            y = window.innerHeight - rect.height - pad
        }
        // Clamp top edge
        if (y < pad) y = pad

        if (x !== pos.x || y !== pos.y) {
            setPos({ x, y })
        }
    }, [open, pos.x, pos.y])

    // ── Close on click-outside or Escape ─────────────────────────────
    useEffect(() => {
        if (!open) return

        const handleClickOutside = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false)
        }
        const handleScroll = () => setOpen(false)

        document.addEventListener('mousedown', handleClickOutside)
        document.addEventListener('keydown', handleKey)
        window.addEventListener('scroll', handleScroll, true)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleKey)
            window.removeEventListener('scroll', handleScroll, true)
        }
    }, [open])

    // ── Right-click handler ──────────────────────────────────────────
    const handleContextMenu = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        showMenu(e.clientX, e.clientY)
    }, [showMenu])

    // ── Long-press handlers (touch) ──────────────────────────────────
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        const touch = e.touches[0]
        const x = touch.clientX
        const y = touch.clientY
        longPressTimer.current = setTimeout(() => {
            showMenu(x, y)
        }, 500)
    }, [showMenu])

    const handleTouchEnd = useCallback(() => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current)
            longPressTimer.current = null
        }
    }, [])

    const handleTouchMove = useCallback(() => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current)
            longPressTimer.current = null
        }
    }, [])

    // ── Long-press handlers (mouse — for Mac single-button) ─────────
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.button !== 0) return // Only left button
        const x = e.clientX
        const y = e.clientY
        longPressTimer.current = setTimeout(() => {
            showMenu(x, y)
        }, 500)
    }, [showMenu])

    const handleMouseUp = useCallback(() => {
        if (longPressTimer.current) {
            clearTimeout(longPressTimer.current)
            longPressTimer.current = null
        }
    }, [])

    // ── Render ───────────────────────────────────────────────────────
    return (
        <>
            <div
                ref={wrapperRef}
                onContextMenu={handleContextMenu}
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                onTouchMove={handleTouchMove}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                style={{ display: 'contents' }}
            >
                {children}
            </div>

            {open && createPortal(
                <div
                    ref={menuRef}
                    className="context-menu"
                    style={{ left: pos.x, top: pos.y }}
                >
                    {items.map((item, i) => (
                        <button
                            key={i}
                            className={`context-menu-item${item.disabled ? ' disabled' : ''}`}
                            onClick={() => {
                                if (!item.disabled) {
                                    item.onClick()
                                    setOpen(false)
                                }
                            }}
                            disabled={item.disabled}
                        >
                            <span className="context-menu-check">
                                {item.checked ? '✓' : ''}
                            </span>
                            {item.icon && <span className="context-menu-icon">{item.icon}</span>}
                            <span className="context-menu-label">{item.label}</span>
                        </button>
                    ))}
                </div>,
                document.body
            )}
        </>
    )
}
